import { Box, Divider, Flex, Grid, IconButton, Image, Text, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, FormControl, FormLabel, Input, Button, useDisclosure, DrawerFooter, Switch, FormErrorMessage, } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { BenefitsAndIDNumberHeaders, EmployeerDetail } from "./moda";
import { useFetch } from "../../../../../hooks/useFetch";
import { CustomToast } from "../../../../../components";
import EditLogo from "../../../../../assets/images/EditLogo.svg";
import IdNumbersLogo from "../../../../../assets/images/IdNumbersLogo.png";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "../../../../../hooks/useAuth";

type props = {
  employeeId: number | null;
  data?: EmployeerDetail;
  onComplete?: any;
};

const DenefitsAndIdNumbers = ({ employeeId, data, onComplete }: props) => {
  const { addToast } = CustomToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { fetchApi } = useFetch(addToast);
  const { hasPermission } = useAuth();
  const [employeerDetail, setEmployeerDetail] = useState<EmployeerDetail | undefined>(data);

  const salaryCompensationSchema: any = yup.object().shape({
    esic_Member: yup.boolean().notRequired(),
    esic_Number: yup.string().when("esic_Member", {
      is: true,
      then: (schema) =>
        schema.required("ESIC number is required").length(10, "ESIC number must be exactly 10 digits"),
      otherwise: (schema) => schema.notRequired(),
    }),
    uan_Number: yup.string().nullable().notRequired().when([], {
      is: (val: any) => val?.length > 0,
      then: (schema) => schema.length(12, "Uan number must be exactly 12 digits"),
    }),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm<EmployeerDetail>({
    resolver: yupResolver(salaryCompensationSchema),
    defaultValues: data,
  });

  const esicMember = watch("esic_Member");

  const onSubmit = async (data: any) => {
    data.empId = employeeId;
    if (!data.esic_Member) {
      data.esic_Number = "";
    }
    let endpoint = `Hrlense_Employee/empBenefits/${employeeId}`;
    const response = await fetchApi(endpoint, "PUT", data, null, "Benifits & ID Numbers Updated Successfully");
    if (response) {
      onComplete();
      onClose();
    }
  };

  // Format ESIC (17 digits grouped 4-4-4-5)
  // function formatEsicNumber(value: string) {
  //   return value.replace(/\D/g, "").slice(0, 17).replace(/(\d{4})(\d{4})(\d{4})(\d{0,5})/, (match, g1, g2, g3, g4) =>
  //     [g1, g2, g3, g4].filter(Boolean).join("-")
  //   );
  // }

  // Format UAN (12 digits grouped 4-3-5)
  function formatUanNumber(value: string) {
    return value.replace(/\D/g, "").slice(0, 12).replace(/(\d{4})(\d{3})(\d{0,5})/, (match, g1, g2, g3) =>
      [g1, g2, g3].filter(Boolean).join("-")
    );
  }

  function handleEditClick() {
    reset({
      esic_Member: data?.esic_Member,
      esic_Number: data?.esic_Number,
      uan_Number: data?.uan_Number,
    });
    onOpen();
  }

  useEffect(() => {
    if (data) {
      setEmployeerDetail(data);
      reset(data);
    }
  }, [data, reset]);

  return (
    <>
      <Box pt={2} mb={6} className="main">
        <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
          <Flex align="center" gap={3}>
            <Image src={IdNumbersLogo} alt={"Benifits & ID Numberss"} />
            <Text className="font-poppins text_sm text_medium col">Benifits & ID Numbers</Text>
          </Flex>
          {hasPermission('Employee', 'UpdateBenifitsDetail') && (<IconButton aria-label="Edit" icon={<Image src={EditLogo} />} variant="none" onClick={() => handleEditClick()} />)}
        </Flex>

        <Box py={2} px={10} bg="#FFFFFF" borderRadius={8}>
          {BenefitsAndIDNumberHeaders.map((data, index) => (
            <Grid key={index} templateColumns="1fr 1fr" alignItems="center" mb={2}>

              <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)" >
                {data?.label}
              </Text>
              <Text className="font-poppins text_sm text_medium col">
                {data?.key === "esic_Number" ? employeerDetail?.[data?.key] || "" : data?.key === "uan_Number" ? formatUanNumber(employeerDetail?.[data?.key] || "") : data?.modifier ? data.modifier(employeerDetail?.[data?.key]) : employeerDetail?.[data?.key]} </Text>
            </Grid>
          ))}
          {/* <Divider mt={2} /> */}
        </Box>
      </Box>

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerContent className="drawer" display="flex" flexDirection="column" height="100%">
            <DrawerCloseButton />
            <DrawerHeader>Update Employment Details</DrawerHeader>
            <DrawerBody pb="70px" flex={1} overflow="auto">
              <Box m={6}>
                <Grid templateColumns="1fr" gap={4}>
                  <FormControl isInvalid={!!errors.esic_Member}>
                    <FormLabel>ESIC Member</FormLabel>
                    <Controller
                      name="esic_Member"
                      control={control}
                      render={({ field }) => (
                        <Switch isChecked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                      )}
                    />
                    <FormErrorMessage>{errors.esic_Member?.message}</FormErrorMessage>
                  </FormControl>

                  {esicMember && (
                    <FormControl isInvalid={!!errors.esic_Number} isRequired>
                      <FormLabel>ESIC Number</FormLabel>
                      <Controller
                        name="esic_Number"
                        control={control}
                        render={({ field }) => (
                          <Input
                            type="text"
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
                          />
                        )}
                      />
                      <FormErrorMessage>{errors.esic_Number?.message}</FormErrorMessage>
                    </FormControl>
                  )}

                  <FormControl isInvalid={!!errors.uan_Number}>
                    <FormLabel>UAN Number</FormLabel>
                    <Controller
                      name="uan_Number"
                      control={control}
                      render={({ field }) => (
                        <Input
                          maxLength={14}
                          type="text"
                          value={formatUanNumber(field.value || "")}
                          onChange={(e) => field.onChange(e.target.value.replace(/\D/g, "").slice(0, 12))}
                        />
                      )}
                    />
                    <FormErrorMessage>{errors.uan_Number?.message}</FormErrorMessage>
                  </FormControl>
                </Grid>
              </Box>
            </DrawerBody>

            <DrawerFooter>
              <Button className="btn_theme" type="submit">Update Details</Button>
            </DrawerFooter>
          </DrawerContent>
        </form>
      </Drawer>
    </>
  );
};

export default DenefitsAndIdNumbers;
